/**
 * Glas-foto's per soort — LOS van de Timmers-API (die foto's hebben het Timmers-logo, dat
 * willen we niet tonen). Standaard tonen we overal een neutrale placeholder; zodra je een
 * eigen foto hebt, zet je 'm in `public/glas-foto/` en voeg je hier de soort-slug → pad toe.
 *
 * De slug is dezelfde als in de URL (`soortSlug`), bv. "floatglas", "mat-glas",
 * "geemailleerd-glas", "spiegels-op-maat".
 */
export const GLASS_PHOTO_PLACEHOLDER = "/glas-placeholder.svg";

/** Isolatieglas-productfoto's — één beeld voor álle dubbel- resp. triple-uitvoeringen. */
export const ISO_DUBBEL_PHOTO = "/glas-foto/isolatieglas-dubbel.webp";
export const ISO_TRIPLE_PHOTO = "/glas-foto/isolatieglas-triple.webp";

/** Eigen foto per glassoort (slug → pad in /public). Leeg = de placeholder. */
export const GLASS_SOORT_PHOTO: Record<string, string> = {
  floatglas: "/glas-foto/floatglas-blank.webp",
  "gelaagd-glas": "/glas-foto/gelaagd.webp",
  // Gehard glas is optisch gewoon helder glas; harden verandert het uiterlijk niet. Een eigen
  // soort-foto hier, zodat de rubriekstegel niet afhangt van welke kleur toevallig vooraan staat —
  // acht van de 42 uitvoeringen (Madras, Mirropane, Diffulit…) hebben nog geen eigen foto.
  "gehard-glas": "/glas-foto/gelaagd.webp",
  // Overzichtsfoto's (matglas-panelen) voor de gelakte/geëmailleerde soorten.
  "geemailleerd-glas": "/glas-foto/geemailleerd-glas.webp",
  "gehard-gelakt-glas": "/glas-foto/gehard-gelakt-glas.webp",
  "matelac-safe": "/glas-foto/matelac-safe.webp",
  "matelac-t": "/glas-foto/matelac-t.webp",
};

/** Foto voor een glassoort: eigen foto als die er is, anders de neutrale placeholder. */
export function glassSoortPhoto(slug: string | undefined): string {
  return (slug && GLASS_SOORT_PHOTO[slug]) || GLASS_PHOTO_PLACEHOLDER;
}

// Deze bestanden worden OOK groot hergebruikt (product-/spec-pagina's, doorsnedetekeningen) —
// de bronbestanden dus niet verkleinen. Voor een kleine tegel (categorie-grid, subcategorie-
// picker, ~150-280px breed) bestaat een aparte, kleinere -tile-variant (scripts/build-images.mjs
// raakt deze statische bestanden niet aan, dus handmatig gegenereerd via sharp).
const TILE_VARIANT = new Set([
  "/glas-foto/floatglas-blank.webp",
  "/glas-foto/vacuumglas-tegel.webp",
  "/glas-foto/isolatieglas-dubbel.webp",
  "/glas-foto/gelaagd.webp",
  "/glas-foto/spiegel-verzilverd-extra-helder.webp",
]);

/** Kleinere variant van een glas-foto voor gebruik als kleine tegel (grid/picker), of
 *  ongewijzigd terug als er geen tile-variant voor bestaat. */
export function glassPhotoTile(img: string): string {
  return TILE_VARIANT.has(img) ? img.replace(/\.webp$/, "-tile.webp") : img;
}

/**
 * Foto voor een glassoort-tegel/nav: eigen soort-foto → anders de foto van de EERSTE uitvoering
 * die er een heeft → anders de placeholder. Zo krijgt bv. Draadglas de foto van zijn eerste
 * uitvoering zolang er nog geen aparte soort-foto is.
 *
 * Bewust "eerste MET foto" en niet "de eerste": bij o.a. figuurglas heeft de eerste uitvoering nog
 * geen foto, en dan bleef de hele tegel op de placeholder staan terwijl de uitvoering eronder wel
 * een foto had. Geef daarom de hele (geordende) kleurlijst mee, niet alleen de eerste.
 */
export function glassSoortImage(slug: string | undefined, colors: string | string[] | undefined): string {
  const list = colors == null ? [] : Array.isArray(colors) ? colors : [colors];
  if (slug && GLASS_SOORT_PHOTO[slug]) return GLASS_SOORT_PHOTO[slug];
  for (const c of list) {
    const foto = glassColorPhoto(slug, c);
    if (foto) return foto;
  }
  return GLASS_PHOTO_PLACEHOLDER;
}

/**
 * Eigen foto per KLEUR/PATROON binnen een soort (soort-slug → exacte kleur-string → pad).
 * Gebruikt in de configurator: het kleurchip-fototje én de hoofd-galerijfoto van de gekozen
 * kleur. De kleur-strings moeten exact matchen met de catalogus (variant.color). Alleen
 * zekere matches; einde-voorraad-kleuren zijn overgeslagen. Zet nieuwe foto's in
 * public/glas-foto/ en voeg hier een regel toe.
 */
export const GLASS_COLOR_PHOTO: Record<string, Record<string, string>> = {
  figuurglas: {
    "Aero": "/glas-foto/figuurglas-aero.webp",
    "Bamboo blank": "/glas-foto/figuurglas-bamboo.webp",
    "Byzantijn Fijn blank / Ornament 58 / Viooltjes Fijn": "/glas-foto/figuurglas-byzantijn-fijn.webp",
    "Byzantijn Grof blank / Ornament 123 / Viooltjes Grof": "/glas-foto/figuurglas-byzantijn-grof.webp",
    "Canale blank / Flutes / Liniar": "/glas-foto/figuurglas-canale.webp",
    "Canale mat/blank / Flutec mat/blank": "/glas-foto/figuurglas-canale-mat.webp",
    "Chinchilla blank / Nerts blank": "/glas-foto/figuurglas-chinchilla.webp",
    "Cotswold blank / Ornament 105": "/glas-foto/figuurglas-cotswold.webp",
    "Crepi blank / Ornament 504 / Klarglas blank": "/glas-foto/figuurglas-crepi.webp",
    // Dubbelzijdige crepi (33/33) = zelfde patroon → zelfde foto.
    "Crepi blank 33/33 / Ornament 033/033 / Klarglas 2-zijdig": "/glas-foto/figuurglas-crepi.webp",
    "Delta blank / Ornament 121": "/glas-foto/figuurglas-delta.webp",
    "Delta mat blank": "/glas-foto/figuurglas-delta-mat.webp",
    "Gegoten Antiek blank / Guss Antiek blank": "/glas-foto/figuurglas-gegoten-antiek.webp",
    "Gothic blank": "/glas-foto/figuurglas-gothic.webp",
    "Kathedraal groot blank / Cathedraal Duits": "/glas-foto/figuurglas-kathedraal-groot.webp",
    "Kathedraal klein blank / Cathedraal Duits": "/glas-foto/figuurglas-kathedraal-klein.webp",
    "Mastercarre blank": "/glas-foto/figuurglas-mastercarre.webp",
    "Master-ligne": "/glas-foto/figuurglas-master-ligne.webp",
    "Master-point": "/glas-foto/figuurglas-master-point.webp",
    "Victoria blank / Niagara blank": "/glas-foto/figuurglas-niagara.webp",
    "Victoria mat blank / Niagara mat blank": "/glas-foto/figuurglas-niagara-mat.webp",
    "Carre nr. 53": "/glas-foto/figuurglas-carre-53.webp",
    "Nylon / Screen / Ornament 59 / Listral K": "/glas-foto/figuurglas-nylon.webp",
    "Silvit blank / Ornament 178 / Boomschors": "/glas-foto/figuurglas-silvit.webp",
    "Soprano": "/glas-foto/figuurglas-soprano.webp",
    "Jan Hagel Blank / Waterdrup / Ornament 521": "/glas-foto/figuurglas-waterdrup.webp",
    "Neoceram (kachelglas)": "/glas-foto/figuurglas-neoceram-kachelglas.webp",
    "Ribglas blank": "/glas-foto/figuurglas-ribglas-blank.webp",
    "Raywall 90T": "/glas-foto/figuurglas-raywall-90t.webp",
  },
  floatglas: {
    "Floatglas blank": "/glas-foto/floatglas-blank.webp",
    // Extra helder = optisch dezelfde heldere plaat → hergebruik de blank-foto.
    "Floatglas extra helder": "/glas-foto/floatglas-blank.webp",
    "Floatglas ontspiegeld": "/glas-foto/floatglas-ontspiegeld.webp",
    "Floatglas 2 zijdig ontspiegeld": "/glas-foto/floatglas-ontspiegeld.webp",
    "Floatglas blauw": "/glas-foto/floatglas-blauw.webp",
    "Floatglas brons": "/glas-foto/floatglas-brons.webp",
    "Floatglas groen": "/glas-foto/floatglas-groen.webp",
    "Floatglas grijs": "/glas-foto/floatglas-grijs.webp",
    "Floatglas dark grey": "/glas-foto/floatglas-dark-grey.webp",
    // Spiegelende coatings → verzilverde-spiegel-foto.
    "Floatglas Mirropane chrome": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
    "Floatglas Mirropane chrome plus": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
    "Floatglas Mirastar": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
  },
  "gelaagd-glas": {
    "Heldere folie": "/glas-foto/gelaagd.webp",
    "Matte folie": "/glas-foto/gelaagd-matte-folie.webp",
    // 1 zijde acide = acide-uiterlijk → acide-foto; geluidswerende folie = optisch als heldere folie.
    "1 zijde acide": "/glas-foto/mat-glas-acide.webp",
    "Geluidswerende folie": "/glas-foto/gelaagd.webp",
    // 33.1 met grijze folie → egaal grijs gelaagd paneel.
    "Blank - 1x folie grijs": "/glas-foto/gelaagd-folie-grijs.webp",
    // 1 zijde figuurglas → dezelfde patroonfoto als bij figuurglas (Flutes = Canale, Screen = Nylon).
    "Figuur 1 zijde chinchilla": "/glas-foto/figuurglas-chinchilla.webp",
    "Figuur 1 zijde crepi": "/glas-foto/figuurglas-crepi.webp",
    "Figuur 1 zijde Raywall 90": "/glas-foto/figuurglas-raywall-90t.webp",
    "Figuur 1 zijde flutes": "/glas-foto/figuurglas-canale.webp",
    "Figuur 1 zijde kathedral klein": "/glas-foto/figuurglas-kathedraal-klein.webp",
    "Figuur 1 zijde canalé": "/glas-foto/figuurglas-canale.webp",
    "Figuur 1 zijde screen": "/glas-foto/figuurglas-nylon.webp",
  },
  "mat-glas": {
    "Acide": "/glas-foto/mat-glas-acide.webp",
    "Acide brons": "/glas-foto/mat-glas-acide-brons.webp",
    "Floatglas dark grey acide": "/glas-foto/mat-glas-dark-grey-acide.webp",
    // Dubbelzijdig acide = zelfde matte look → hergebruik de acide-foto.
    "Acide dubbelzijdig": "/glas-foto/mat-glas-acide.webp",
    "Floatglas acide extra helder": "/glas-foto/mat-glas-acide-extra-helder.webp",
    "Floatglas acide grijs": "/glas-foto/mat-glas-acide-grijs.webp",
    "Acide groen": "/glas-foto/mat-glas-acide-groen.webp",
  },
  draadglas: {
    "Draadglas-E (gefigureerd, gebobbeld, Engels, E, S)": "/glas-foto/draadglas-engels.webp",
    "Draadglas (glad, Duits, Brute, N)": "/glas-foto/draadglas-normaal.webp",
  },
  "lacobel-safe": {
    "Starlight black (0337)": "/glas-foto/lacobel-safe-starlight-black.webp",
    "Rich aluminium (9007)": "/glas-foto/lacobel-safe-rich-aluminium.webp",
    "Metal taupe (0627)": "/glas-foto/lacobel-safe-metal-taupe.webp",
    "Soft white (9010)": "/glas-foto/lacobel-safe-soft-white.webp",
    "Pure white / Extra helder (9003)": "/glas-foto/lacobel-safe-pure-white.webp",
    "Light Brown (1236)": "/glas-foto/lacobel-safe-light-brown.webp",
    "Pearl white (1013)": "/glas-foto/lacobel-safe-pearl-white.webp",
    "Anthracite Authentic (7016)": "/glas-foto/lacobel-safe-anthracite-authentic.webp",
    "Classic Black (9005)": "/glas-foto/lacobel-safe-classic-black.webp",
  },
  "lacobel-t": {
    "Zen grey (6005)": "/glas-foto/lacobel-t-zen-grey.webp",
    "Cool white (1502)": "/glas-foto/lacobel-t-cool-white.webp",
    "Misty white (5813)": "/glas-foto/lacobel-t-misty-white.webp",
    "Oyster white (0613)": "/glas-foto/lacobel-t-oyster-white.webp",
    "Crisp white (1000)": "/glas-foto/lacobel-t-crisp-white.webp",
    "Moka (3113)": "/glas-foto/lacobel-t-moka.webp",
    "Anthracite grey (0913)": "/glas-foto/lacobel-t-anthracite-grey.webp",
    "Deep black (8502)": "/glas-foto/lacobel-t-deep-black.webp",
  },
  "spiegels-op-maat": {
    "Verzilverd acide": "/glas-foto/spiegel-verzilverd-acide.webp",
    "Verzilverd extra helder": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
    // Blank verzilverd = zelfde heldere spiegel als extra helder → zelfde foto.
    "Blank verzilverd": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
    "Blank verzilverd + veiligheidsfolie": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
  },
  // Gehard glas (hardglas) — heldere uitvoeringen lenen de helder-(gelaagd)-foto; álle acide-
  // uitvoeringen de acide-foto.
  "gehard-glas": {
    "Helder": "/glas-foto/gelaagd.webp",
    "Extra helder": "/glas-foto/gelaagd.webp",
    "Acide helder": "/glas-foto/mat-glas-acide.webp",
    "Acide extra helder": "/glas-foto/mat-glas-acide.webp",
    "Acide luxclear protect": "/glas-foto/mat-glas-acide.webp",
    // Getinte acide-uitvoeringen — zelfde foto's als bij mat-glas (acide groen/grijs).
    "Acide groen": "/glas-foto/mat-glas-acide-groen.webp",
    "Acide grijs": "/glas-foto/mat-glas-acide-grijs.webp",
    // Timeless coating = optisch helder → helder-foto.
    "Timeless coating helder": "/glas-foto/gelaagd.webp",
    // Getinte uitvoeringen — zelfde tint als het gelijknamige floatglas. Harden verandert de
    // kleur van de plaat niet, dus de float-foto klopt ook voor de geharde uitvoering.
    "Blauw": "/glas-foto/floatglas-blauw.webp",
    "Brons": "/glas-foto/floatglas-brons.webp",
    "Groen": "/glas-foto/floatglas-groen.webp",
    "Grijs": "/glas-foto/floatglas-grijs.webp",
    // Ontspiegeld + spiegelende/heldere coatings.
    "2 zijdig ontspiegeld": "/glas-foto/floatglas-ontspiegeld.webp",
    "Mirastar": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
    "Helder luxclear protect": "/glas-foto/gelaagd.webp",
    // Figuur-uitvoeringen — zelfde patronen als figuurglas → gelijknamige figuurglas-foto's.
    "Crepi blank": "/glas-foto/figuurglas-crepi.webp",
    "Chinchilla blank": "/glas-foto/figuurglas-chinchilla.webp",
    "Matercarre": "/glas-foto/figuurglas-mastercarre.webp",
    "Masterpoint": "/glas-foto/figuurglas-master-point.webp",
    "Masterligne": "/glas-foto/figuurglas-master-ligne.webp",
    "Nylon": "/glas-foto/figuurglas-nylon.webp",
    "Silvit blank": "/glas-foto/figuurglas-silvit.webp",
    "Byzantijn fijn blank": "/glas-foto/figuurglas-byzantijn-fijn.webp",
    "Byzantijn grof blank": "/glas-foto/figuurglas-byzantijn-grof.webp",
    "Canale blank": "/glas-foto/figuurglas-canale.webp",
    "Delta blank": "/glas-foto/figuurglas-delta.webp",
    "Kathedraal blank": "/glas-foto/figuurglas-kathedraal-groot.webp",
    "Jan Hagel blank": "/glas-foto/figuurglas-waterdrup.webp",
    "Raywall": "/glas-foto/figuurglas-raywall-90t.webp",

    // ── Kleurnamen zoals de Timmers-API ze levert ───────────────────────────────────────────
    // De namen hierboven komen uit onze handmatige gehard-glassoort (prijslijst-PDF); sinds de
    // sync van 03-10-2026 levert de API gehard glas óók, maar met eigen namen ("Gehard blank" in
    // plaats van "Helder"). Daardoor viel de hele rubriek terug op de placeholder-afbeelding.
    // Harden verandert de kleur van de plaat niet, dus dit zijn dezelfde foto's als bij float-,
    // mat- en figuurglas. (12-10-2026.)
    "Gehard blank": "/glas-foto/gelaagd.webp",
    "Gehard extra helder": "/glas-foto/gelaagd.webp",
    "Gehard blank Luxclear coating": "/glas-foto/gelaagd.webp",
    "Gehard helder Timeless": "/glas-foto/gelaagd.webp",
    "Gehard extra helder Timeless": "/glas-foto/gelaagd.webp",
    "Gehard glas blank - 1.0 coating": "/glas-foto/gelaagd.webp",
    "Gehard acide": "/glas-foto/mat-glas-acide.webp",
    "Gehard acide extra helder": "/glas-foto/mat-glas-acide-extra-helder.webp",
    "Gehard acide Luxclear coating": "/glas-foto/mat-glas-acide.webp",
    "Gehard groen acide": "/glas-foto/mat-glas-acide-groen.webp",
    "Gehard grijs acide": "/glas-foto/mat-glas-acide-grijs.webp",
    "Gehard brons acide": "/glas-foto/mat-glas-acide-brons.webp",
    "Gehard blauw": "/glas-foto/floatglas-blauw.webp",
    "Gehard brons": "/glas-foto/floatglas-brons.webp",
    "Gehard brons 33/33": "/glas-foto/floatglas-brons.webp",
    "Gehard glas brons - 1.0 coating": "/glas-foto/floatglas-brons.webp",
    "Gehard groen": "/glas-foto/floatglas-groen.webp",
    "Gehard grijs": "/glas-foto/floatglas-grijs.webp",
    "Gehard Mirastar": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
    // Figuur-uitvoeringen: zelfde patroon als het gelijknamige figuurglas.
    "Gehard Byzantijn Fijn blank / Orna 528 / Viooltjes fijn": "/glas-foto/figuurglas-byzantijn-fijn.webp",
    "Gehard Byzantijn Grof blank / Viooltjes grof / Ornament 123 & 523": "/glas-foto/figuurglas-byzantijn-grof.webp",
    "Gehard Canalé / Flutes / Liniar": "/glas-foto/figuurglas-canale.webp",
    "Gehard Canalé mat / blank 5 mm": "/glas-foto/figuurglas-canale-mat.webp",
    "Gehard Chinchilla": "/glas-foto/figuurglas-chinchilla.webp",
    "Gehard Crepi": "/glas-foto/figuurglas-crepi.webp",
    "Gehard Crepi / Ornament 504 / Klarglas blank": "/glas-foto/figuurglas-crepi.webp",
    "Gehard Delta blank / Ornament 121": "/glas-foto/figuurglas-delta.webp",
    "Gehard Gothic blank": "/glas-foto/figuurglas-gothic.webp",
    "Gehard Kathedraal blank": "/glas-foto/figuurglas-kathedraal-groot.webp",
    "Gehard Mastercarre": "/glas-foto/figuurglas-mastercarre.webp",
    "Gehard Masterligne": "/glas-foto/figuurglas-master-ligne.webp",
    "Gehard Masterpoint": "/glas-foto/figuurglas-master-point.webp",
    "Gehard Nylon / Screen / Ornament 59 / Listral K": "/glas-foto/figuurglas-nylon.webp",
    "Gehard Raywall 90T": "/glas-foto/figuurglas-raywall-90t.webp",
  },
  // Installatieglas — helder + extra helder lenen de helder-(gelaagd)-foto; acide-uitvoeringen de
  // acide-foto; de figuur-uitvoeringen de gelijknamige figuurglas-foto's (Timmers levert dezelfde
  // patronen als installatieglas).
  installatieglas: {
    "Helder": "/glas-foto/gelaagd.webp",
    "Extra helder": "/glas-foto/gelaagd.webp",
    // Blank/Timeless = optisch helder → dezelfde helder-foto.
    "Blank, Timeless": "/glas-foto/gelaagd.webp",
    "Acide blank": "/glas-foto/mat-glas-acide.webp",
    "Acide extra helder": "/glas-foto/mat-glas-acide.webp",
    "Acide dubbelzijdig blank": "/glas-foto/mat-glas-acide.webp",
    // Getinte acide-uitvoeringen — zelfde foto's als bij mat-glas (acide groen/grijs/brons).
    "Acide groen": "/glas-foto/mat-glas-acide-groen.webp",
    "Acide grijs": "/glas-foto/mat-glas-acide-grijs.webp",
    "Acide brons": "/glas-foto/mat-glas-acide-brons.webp",
    "Acide dark grey": "/glas-foto/mat-glas-dark-grey-acide.webp",
    // Getinte uitvoeringen — zelfde tint als het gelijknamige floatglas.
    "Blauw": "/glas-foto/floatglas-blauw.webp",
    "Brons": "/glas-foto/floatglas-brons.webp",
    "Groen": "/glas-foto/floatglas-groen.webp",
    "Grijs": "/glas-foto/floatglas-grijs.webp",
    "Dark grey": "/glas-foto/floatglas-dark-grey.webp",
    // Spiegelende/heldere coatings.
    "Mirastar": "/glas-foto/spiegel-verzilverd-extra-helder.webp",
    "Helder Luxclear coating": "/glas-foto/gelaagd.webp",
    "Crepi helder": "/glas-foto/figuurglas-crepi.webp",
    "Chinchilla": "/glas-foto/figuurglas-chinchilla.webp",
    "Matercarre": "/glas-foto/figuurglas-mastercarre.webp",
    "Masterpoint": "/glas-foto/figuurglas-master-point.webp",
    "Masterligne": "/glas-foto/figuurglas-master-ligne.webp",
    "Nylon": "/glas-foto/figuurglas-nylon.webp",
    "Raywall 90T": "/glas-foto/figuurglas-raywall-90t.webp",
  },
  // Gehard-gelaagd glas — de 2× heldere uitvoering leent de gelaagd-foto.
  "gehard-gelaagd-glas": {
    "2x Helder": "/glas-foto/gelaagd.webp",
  },
  // Beloopbaar glas (gelaagd) — heldere folie = gelaagd-foto, matte folie = matte gelaagd-foto.
  "beloopbaar-glas": {
    "Heldere folie": "/glas-foto/gelaagd.webp",
    "Matte folie": "/glas-foto/gelaagd-matte-folie.webp",
  },
};

/** Foto van een specifieke kleur/patroon (of undefined als er nog geen eigen foto is). */
export function glassColorPhoto(soortSlug: string | undefined, color: string | undefined): string | undefined {
  return (soortSlug && color && GLASS_COLOR_PHOTO[soortSlug]?.[color]) || undefined;
}
